<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { formatAmountWithCustomerConfig, shouldIgnoreRowClick } from '@peanuds/shared';
import { mapRowToDialogSelection, type TransactionDetailsDialogSelection } from '@peanuds/modules';
import { useCustomerConfig } from '@peanuds/app-shell';
import {
  ChartBarsVertical2Icon as ChartBarVerticalIcon,
  FileBlankIcon as EmptyIcon,
  UiButton,
  UiDataTable,
} from '@peanuds/ui';
import { useLazyLoadedRowsPagination } from '@peanuds/composables';
import { useControlSpendingsCardRows } from '../composables/useControlSpendingsCardRows';
import { useControlSpendingsTableFeed } from '../composables/useControlSpendingsTableFeed';
import { useControlSpendingsTableRows } from '../composables/useControlSpendingsTableRows';
import { getControlSpendingColumns } from '../internal/control-spendings-table.columns';
import type { FilterableControlSpendingTableRow } from '../internal/control-spendings-table.filters';
import type { ControlSpendingsTableFilter } from '../internal/control-spendings-table.types';

export type TableFilter = ControlSpendingsTableFilter;

const emit = defineEmits<{
  'transaction-click': [selection: TransactionDetailsDialogSelection];
  'card-transaction-click': [transactionId: string];
}>();

const { search = '', filter = 'all-transactions' } = defineProps<{
  search?: string;
  filter?: TableFilter;
}>();

const { t } = useTranslation();
const router = useRouter();
const { currency } = useCustomerConfig();

const {
  pageSize,
  resolvedTransactionAccountIds,
  transactions,
  isTransactionsLoading,
  transactionsError,
  hasMore,
  loadMore,
} = useControlSpendingsTableFeed({
  filter: () => filter,
});

const cardRows = useControlSpendingsCardRows();

const { filteredRows, totalRecords, hasReferenceDetails } = useControlSpendingsTableRows({
  transactions,
  cardRows,
  filter: () => filter,
  search: () => search,
});

const hasTransactionsError = computed(() => transactionsError.value !== undefined);
const {
  currentPageStart,
  visibleRows,
  displayTotalRecords: tableTotalRecords,
  handlePageChange,
} = useLazyLoadedRowsPagination({
  rows: filteredRows,
  pageSize,
  hasMore,
  loadMore,
  resetSources: [() => filter, () => search, () => resolvedTransactionAccountIds.value.join(',')],
});

const translatedColumns = computed(() => {
  return getControlSpendingColumns(
    hasReferenceDetails.value,
    {
      formatter: (row) => formatAmountWithCustomerConfig(row, currency.value.transactionAmount),
    },
    search,
  ).map((column) => ({
    ...column,
    header: t(column.header),
  }));
});

const isLargeScreen = ref(false);
const updateMedia = () => {
  isLargeScreen.value = window.matchMedia('(min-width: 80rem)').matches;
};

onMounted(() => {
  updateMedia();
  window.addEventListener('resize', updateMedia);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateMedia);
});

const handleOpenAnalytics = () => {
  const accountId = resolvedTransactionAccountIds.value[0];

  router.push({
    name: 'analytics',
    query: accountId ? { accountId } : undefined,
  });
};

const handleRowClick = (event: {
  originalEvent?: Event;
  data: FilterableControlSpendingTableRow;
}) => {
  if (shouldIgnoreRowClick(event.originalEvent)) return;

  const { cardMerchantName, transactionId } = event.data;
  if (cardMerchantName !== undefined && transactionId !== undefined) {
    emit('card-transaction-click', transactionId);
    return;
  }

  emit('transaction-click', mapRowToDialogSelection(event.data));
};
</script>

<template>
  <div class="cs-table">
    <div
      v-if="!isTransactionsLoading && hasTransactionsError && totalRecords === 0"
      class="cs-table__empty"
    >
      <div class="cs-table__empty-icon">
        <EmptyIcon />
      </div>
      <p class="cs-table__empty-text">{{ t('controlSpending.table.load_error') }}</p>
    </div>

    <template v-else-if="isTransactionsLoading || totalRecords > 0 || hasMore">
      <div v-if="!isTransactionsLoading && hasTransactionsError" class="cs-table__warning">
        {{ t('controlSpending.table.load_error') }}
      </div>

      <UiDataTable
        :loading="isTransactionsLoading"
        :columns="translatedColumns"
        sortField="dateAndTime"
        :value="visibleRows"
        :first="currentPageStart"
        :rows="pageSize"
        :total-records="tableTotalRecords"
        :show-first-last-page-links="false"
        lazy
        class="cs-table__grid"
        data-testid="dashboard-control-spendings-table"
        scrollable
        scrollHeight="flex"
        @page="handlePageChange"
        @row-click="handleRowClick"
      >
        <template #paginatorend>
          <div class="cs-table__paginator-actions">
            <UiButton
              class="cs-table__analytics-button"
              :label="isLargeScreen ? t('controlSpending.open_analytics') : undefined"
              variant="outlined"
              @click="handleOpenAnalytics"
            >
              <template #icon>
                <ChartBarVerticalIcon />
              </template>
            </UiButton>
          </div>
        </template>
      </UiDataTable>
    </template>

    <div v-else class="cs-table__empty">
      <div class="cs-table__empty-icon">
        <EmptyIcon />
      </div>
      <p class="cs-table__empty-text">{{ t('controlSpending.table.no_results') }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cs-table {
  display: flex;
  min-height: 0;
  flex-direction: column;
  height: 100%;

  &__grid {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  :deep(.p-datatable-table-container),
  :deep(.p-datatable-wrapper) {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  :deep(.ui-table-skeleton) {
    flex: 1;
    width: 100%;
  }

  :deep(.ui-table-skeleton .p-datatable),
  :deep(.ui-table-skeleton .p-datatable-table) {
    width: 100%;
  }

  :deep(.p-datatable-paginator-bottom) {
    margin-inline: -$static-space-4;
    padding-inline: $static-space-4;
  }

  &__analytics-button {
    padding: $static-space-2 $static-space-2;
    // Aura outlines with primary-200; match the label so the button reads as one colour.
    --p-button-outlined-primary-border-color: var(--p-primary-color);
    font-size: $static-text-md;
    font-weight: $font-weight-medium;
    letter-spacing: var(--p-letter-spacing-body);
    margin-inline: -9px;
  }

  &__paginator-actions {
    display: flex;
    gap: $static-space-2;
    align-items: center;
  }

  &__warning {
    margin-bottom: $static-space-2;
    padding: $static-space-2 $static-space-4;
    border-radius: var(--p-border-radius-xl);
    background-color: var(--p-orange-50);
    color: var(--p-orange-700);
    font-size: $static-text-sm;
    font-weight: $font-weight-medium;
  }

  &__amount-header :deep(.p-datatable-column-header-content) {
    justify-content: flex-end;
  }

  &__amount-header :deep(.p-datatable-column-title) {
    margin-left: auto;
  }

  :deep(.cs-table__reference-header),
  :deep(.cs-table__reference-cell) {
    width: 6rem;
    max-width: 6rem;
  }

  :deep(.cs-table__reference-cell) {
    overflow: hidden;
  }

  :deep(.p-datatable-tbody > tr > td) {
    padding: 10.5px $static-space-4;
  }

  :deep(.p-datatable-tbody > tr) {
    cursor: pointer;
  }

  :deep(.cs-table__date-header.p-datatable-sortable-column),
  :deep(.cs-table__date-header.p-datatable-sortable-column:not(.p-datatable-column-sorted):hover),
  :deep(.cs-table__date-header.p-datatable-column-sorted) {
    background: var(--p-datatable-header-cell-background);
    color: var(--p-datatable-header-cell-color);
  }

  :deep(.cs-table__date-header .p-datatable-column-title) {
    color: inherit;
  }

  :deep(.cs-table__date-header .p-datatable-sort-icon) {
    display: none;
  }

  &__empty {
    display: flex;
    flex: 0;
    margin-top: $static-space-2;
    padding: $static-space-6 0;
    gap: $static-space-3;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-color: $color-bg-canvas;
    border-radius: var(--p-border-radius-xl);

    &-icon {
      width: $static-space-10;
      height: $static-space-10;
      border-radius: var(--p-border-radius-absolut);
      background-color: $color-base-grey;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-text {
      margin: 0;
      color: $color-text-primary;
      font-size: $static-text-md;
      line-height: $leading-none;
    }
  }
}
</style>
